import { RichTextDisplay, StatBar } from '@/components/shared';
import { useArtifactContext } from '@/pages/artifacts/useArtifactContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@startupquickstart/frontend';
import { CircleDot, Clock, Hash, Radar, Shield, Target } from 'lucide-react';

export function ArtifactOverviewTab() {
  const { artifact, isLoading } = useArtifactContext();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
    );
  }

  if (!artifact) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Artifact not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <StatBar
        items={[
          { label: 'Detection Radius', value: `${artifact.detectionRadius}m`, icon: Radar },
          { label: 'Claim Radius', value: `${artifact.claimRadius}m`, icon: CircleDot },
          {
            label: 'Effect Duration',
            value: artifact.effectDuration ? `${artifact.effectDuration} min` : 'Instant',
            icon: Clock,
          },
          { label: 'Effect Value', value: artifact.effectValue ?? 'N/A', icon: Hash },
        ]}
      />

      {/* Description */}
      {artifact.description && (
        <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
          <CardHeader className="px-6 py-4 !pb-4 border-b">
            <CardTitle>Description</CardTitle>
            <CardDescription>Flavor text shown when artifact is claimed</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <RichTextDisplay content={artifact.description} />
          </CardContent>
        </Card>
      )}

      {/* Settings */}
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <CardTitle>Settings</CardTitle>
          <CardDescription>Claim mode and targeting</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Target Count</p>
                <p className="text-sm text-muted-foreground">
                  {artifact.targetCount === 0 ? 'All teams' : `${artifact.targetCount} team${artifact.targetCount !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Claim Mode</p>
                <p className="text-sm text-muted-foreground">
                  {artifact.claimMode === 'first_claim' ? 'First team to claim only' : 'All teams can claim'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gate Challenge */}
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <CardTitle>Gate Challenge</CardTitle>
          <CardDescription>Optional question before claiming</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {artifact.gateEnabled ? (
            <div className="space-y-4">
              {artifact.gateClue && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Clue</h4>
                  <RichTextDisplay content={artifact.gateClue} />
                </div>
              )}
              {artifact.gateAnswer && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Answer</h4>
                  <p className="text-sm font-mono bg-muted/50 px-3 py-2 rounded-lg">
                    {artifact.gateAnswer}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No gate challenge — teams can claim immediately</p>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
